import { useState, startTransition, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './sec.css';
import supabase from '../../supabase';
import { Helmet } from 'react-helmet-async';

export default function ClaimBusiness() {
  const navigate = useNavigate();
  const location = useLocation();

  // email split: local part before @, domain after @ (from www)
  const [emailLocal, setEmailLocal] = useState('');
  const [emailDomain, setEmailDomain] = useState('');

  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [referral, setReferral] = useState('');
  const [detectedCountry, setDetectedCountry] = useState(null);

  const referralActivatedAt = new Date().toISOString();

  const countries = [
    'United States', 'United Kingdom', 'Canada', 'Australia',
    'Germany', 'France', 'Japan', 'India', 'Brazil', 'Other'
  ];

  const categories = [
    'Bank', 'Jewelry Store', 'Clothing Store', 'Car Dealership',
    'Software', 'Travel Agency', 'Business Services', 'Fitness Services',
    'Furniture Store', 'Pet Store', 'Insurance Agency', 'Home Services',
    'Education', 'Electronics', 'Cosmetics Store', 'Real Estate'
  ];

  // helper: extract domain from website URL (accepts full URL or plain domain)
  const getDomainFromUrl = (raw) => {
    if (!raw) return '';

    let urlString = raw.trim();

    // If no protocol, add https:// so new URL() works
    if (!/^https?:\/\//i.test(urlString)) {
      urlString = `https://${urlString}`;
    }

    try {
      const url = new URL(urlString);
      let host = url.hostname.toLowerCase(); // e.g. "www.example.com"

      if (host.startsWith('www.')) {
        host = host.slice(4); // "example.com"
      }

      return host;
    } catch (e) {
      return '';
    }
  };

  // when opened from claim button: ?url=&title=&category=
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlFromQuery = params.get('url');
    const titleFromQuery = params.get('title');
    const categoryFromQuery = params.get('category');

    if (titleFromQuery) setCompanyName(titleFromQuery);
    if (urlFromQuery) setCompanyWebsite(urlFromQuery);
    if (categoryFromQuery) setCategory(categoryFromQuery);
  }, [location.search]);

  // derive emailDomain automatically from companyWebsite
  useEffect(() => {
    if (!companyWebsite) {
      setEmailDomain('');
      return;
    }
    const domain = getDomainFromUrl(companyWebsite);
    setEmailDomain(domain);
  }, [companyWebsite]);

  // detect country (same as before)
  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        setDetectedCountry(data.country_name);
        setCountry(data.country_name);
      })
      .catch(err => console.error('Geolocation error:', err));
  }, []);

  const goToSignIn = () => {
    startTransition(() => navigate('/signin'));
  };

  const handleSignUp = async (e) => {
  e.preventDefault();
  setLoading(true);
  setErrorMsg('');
  setSuccessMessage('');

  const local = emailLocal.trim();
  if (!local) {
    setErrorMsg('Please enter the email name (before @).');
    setLoading(false);
    return;
  }
  if (!emailDomain) {
    setErrorMsg('Invalid or missing website / domain.');
    setLoading(false);
    return;
  }

  const fullEmail = `${local}@${emailDomain}`;

  const siteDomain = getDomainFromUrl(companyWebsite);
  if (!siteDomain) {
    setErrorMsg('Please enter a valid company website (including https://).');
    setLoading(false);
    return;
  }

  const emailDomainNow = fullEmail.split('@')[1]?.toLowerCase();
  if (!emailDomainNow || !emailDomainNow.endsWith(siteDomain)) {
    setErrorMsg(
      `Email must belong to the same domain as the website (e.g. something@${siteDomain}).`
    );
    setLoading(false);
    return;
  }

  try {
    // 🔹 1) Sign up user ONLY, store all info in user_metadata
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: fullEmail,
      password,
      options: {
        data: {
          name,
          lastname,
          isBusiness: true,
          companyName,
          companyWebsite,
          category,
          phone,
          country,
          referral,
          referralActivatedAt, // you defined this above
          claim_mode: true,    // mark this came from ClaimBusiness
        },
      },
    });

    if (signUpError) throw signUpError;

    const user = signUpData?.user;
    if (!user) {
      setErrorMsg('Signup failed. Please try again.');
      setLoading(false);
      return;
    }

    // 🔹 No insert into businesses here – that will happen after email verification

    setSuccessMessage(
      'Account created! Please check your email and verify your address to activate your business.'
    );

    // Reset fields (keep website/category from claim if you want)
    setEmailLocal('');
    setPassword('');
    setName('');
    setLastname('');
    setCompanyName('');
    setPhone('');
    setCountry('');
    setReferral('');

    // You CAN sign out, but not required. After clicking email link, Supabase will sign them in again.
    await supabase.auth.signOut();
  } catch (err) {
    setErrorMsg(err.message || 'Something went wrong.');
  } finally {
    setLoading(false);
  }
};


  return (
    <div className='w-full px-[14px] ip:px-[25px] 2xl:px-[96px] xl:px-[64px] lg:px-[64px] sp:px-[18px] bg-[#1E7D6B] flex justify-center h-auto pb-[50px] pt-[80px]'>
<Helmet>
  <title>Sitemanifest </title> {/* e.g., Login or Dashboard */}
  
  {/* The directive to stay out of search results */}
  <meta name="robots" content="noindex, nofollow" />
  
  {/* Prevent search engines from showing a "cached" version of a private dashboard */}
  <meta name="googlebot" content="noarchive" />
</Helmet>
      {/* Mobile View */}
      <div className='w-full bg-registerbg xs:hidden h-auto px-[17px] ip:px-[20px] sp:px-[14px] pb-[15px] flex bg-[#1C6065] bg-opacity-95 rounded-[12px]'>
        <div className='flex py-[16px] flex flex-col items-center w-full'>
          <div className='flex flex-col w-full gap-[16px] items-center'>
            <h1 className='font-tenali text-white text-[22px] ip:text-[25px] tracking-[1px] font-regular'>
              Claim your business
            </h1>
          </div>

          <form onSubmit={handleSignUp} className='flex gap-[10px] ip:gap-[15px] mt-[20px] w-full flex-col'>
            <input
              type="text"
              placeholder="First Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light placeholder-white text-white bg-opacity-30 ip:h-[45px] h-[40px] rounded-[8px] pl-[12px] focus:outline-none"
            />

            <input
              type="text"
              placeholder="Last Name"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              required
              className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light placeholder-white text-white bg-opacity-30 ip:h-[45px] h-[40px] rounded-[8px] pl-[12px] focus:outline-none"
            />

            {/* Email split: [local] @domain – mobile */}
            <div className="w-full flex items-center bg-[#d9d9d9] bg-opacity-30 rounded-[8px] pl-[12px] pr-[12px] ip:h-[45px] h-[40px]">
              <input
                type="text"
                placeholder="info"
                value={emailLocal}
                onChange={(e) => setEmailLocal(e.target.value)}
                required
                className="flex-1 bg-transparent text-[14px] ip:text-[16px] font-jakarta font-light placeholder-white text-white focus:outline-none"
              />
              <span className="text-white text-[14px] ip:text-[16px] font-jakarta ml-1">
                @{emailDomain || 'your-domain.com'}
              </span>
            </div>

            {/* Company name – editable */}
            <input
              type="text"
              placeholder="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light placeholder-white text-white bg-opacity-30 ip:h-[45px] h-[40px] rounded-[8px] pl-[12px] focus:outline-none"
            />

            {/* Website – prefilled from claim, not editable */}
            <input
              type="url"
              placeholder="Company website (https://example.com)"
              value={companyWebsite}
              readOnly
              className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light placeholder-white text-white bg-opacity-30 ip:h-[45px] h-[40px] rounded-[8px] pl-[12px] focus:outline-none opacity-60 cursor-not-allowed"
            />

            {/* Phone */}
            <input
              type="tel"
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light placeholder-white text-white bg-opacity-30 ip:h-[45px] h-[40px] rounded-[8px] pl-[12px] focus:outline-none"
            />

            <input
              type="text"
              placeholder="Referral"
              value={referral}
              onChange={(e) => setReferral(e.target.value)}
              className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light placeholder-white text-white bg-opacity-30 ip:h-[45px] h-[40px] rounded-[8px] pl-[12px] focus:outline-none"
            />

            {/* Country – editable */}
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light text-white bg-opacity-30 ip:h-[45px] h-[40px] rounded-[8px] pl-[12px] focus:outline-none"
            >
              <option value="" disabled>Select Country</option>
              {countries.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>

            {/* Category – prefilled from claim, not editable */}
            <select
              value={category}
              disabled
              className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light text-white bg-opacity-30 ip:h-[45px] h-[40px] rounded-[8px] pl-[12px] focus:outline-none opacity-60 cursor-not-allowed"
            >
              <option value="" disabled>Select Business Category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light placeholder-white text-white bg-opacity-30 ip:h-[45px] h-[40px] rounded-[8px] pl-[12px] focus:outline-none"
            />

            <div className='items-start flex w-full'>
              <div className='flex flex-row items-center gap-[20px] mt-[6px] mb-[6px] pl-[3px]'>
                <input 
                  type="checkbox" 
                  id="terms" 
                  required 
                  className="w-[24px] h-[24px] rounded-[5px] bg-[#d9d9d9]"
                />
                <label htmlFor="terms" className='font-poppins text-[11px] text-white tracking-[1px] font-light'>
                  I agree to the <span className='underline text-[#7CD998]'>Terms & Condition</span>
                </label>
              </div>
            </div>

            <div className='flex flex-row w-full items-start text-start pl-[3px] gap-[15px]'>
              <h2 className='font-tenali text-[18px] tracking-[1px] text-white font-regular'>Already have an account?</h2>
              <span onClick={goToSignIn} className='text-[#7CD998] font-tenali underline text-[18px] tracking-[1px] cursor-pointer'>
                Log in
              </span>
            </div>
            
            <button
              type="submit"
              className="w-full bg-[#1E7D6B] h-[45px] rounded-[8px] font-jakarta text-[15px] font-semibold tracking-[1px] text-white disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create Business Account'}
            </button>
            
            {errorMsg && (
              <p className="text-red-500 text-sm text-center py-2">{errorMsg}</p>
            )}
            {successMessage && (
              <p className="text-green-500 text-sm text-center py-2">{successMessage}</p>
            )}
          </form>
        </div>
      </div>

      {/* Desktop View */}
      <div className='hidden bg-registerbg 2xl:max-w-[1280px] xs:max-w-[540px] lg:max-w-[740px] xl:max-w-full 2xl:mx-auto p-[25px] lg:mt-[30px] xs:flex w-full 2xl:h-auto xl:px-[40px] lg:px-[30px] lg:h-auto lg:py-[40px] bg-[#1C6065] bg-opacity-95 rounded-[12px] flex-col'>
        <div className='flex 2xl:max-w-[550px] 2xl:mx-auto flex-row w-full gap-[25px] xl:gap-[60px] lg:gap-[40px]'>
          <div className='w-full flex-col items-center flex'>
            <div className='flex flex-col w-full gap-[px] items-center'>
              <h1 className='font-tenali text-white xs:text-[35px] lg:text-[42px] tracking-[1px] font-regular'>
                Claim your business
              </h1>
              <div className='flex flex-row w-full justify-center items-center gap-[15px]'>
                <h2 className='font-tenali text-[18px] xs:text-[20px] lg:text-[22px] tracking-[1px] text-white font-regular'>
                  Already have an account?
                </h2>
                <span
                  onClick={goToSignIn}
                  className='text-[#7CD998] font-tenali lg:text-[22px] underline text-[18px] tracking-[1px] cursor-pointer'
                >
                  Log in
                </span>
              </div>

              <form onSubmit={handleSignUp} className='flex gap-[15px] mt-[20px] w-full flex-col'>
                <div className='flex flex-row w-full gap-[20px]'>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full bg-[#d9d9d9] placeholder-white text-white bg-opacity-30 h-[50px] rounded-[8px] pl-[12px] focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastname}
                    onChange={(e) => setLastname(e.target.value)}
                    required
                    className="w-full bg-[#d9d9d9] placeholder-white text-white bg-opacity-30 h-[50px] rounded-[8px] pl-[12px] focus:outline-none"
                  />
                </div>

                {/* Email split – desktop */}
                <div className="w-full flex items-center bg-[#d9d9d9] bg-opacity-30 rounded-[8px] pl-[12px] pr-[12px] h-[50px]">
                  <input
                    type="text"
                    placeholder="info"
                    value={emailLocal}
                    onChange={(e) => setEmailLocal(e.target.value)}
                    required
                    className="flex-1 bg-transparent placeholder-white text-white focus:outline-none"
                  />
                  <span className="text-white font-jakarta ml-1">
                    @{emailDomain || 'your-domain.com'}
                  </span>
                </div>

                <input
                  type="text"
                  placeholder="Company name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="w-full bg-[#d9d9d9] placeholder-white text-white bg-opacity-30 h-[50px] rounded-[8px] pl-[12px] focus:outline-none"
                />

                <input
                  type="url"
                  placeholder="Company website (https://example.com)"
                  value={companyWebsite}
                  readOnly
                  className="w-full bg-[#d9d9d9] placeholder-white text-white bg-opacity-30 h-[50px] rounded-[8px] pl-[12px] focus:outline-none opacity-60 cursor-not-allowed"
                />

                <input
                  type="tel"
                  placeholder="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="w-full bg-[#d9d9d9] placeholder-white text-white bg-opacity-30 h-[50px] rounded-[8px] pl-[12px] focus:outline-none"
                />

                <input
                  type="text"
                  placeholder="Referral"
                  value={referral}
                  onChange={(e) => setReferral(e.target.value)}
                  className="w-full bg-[#d9d9d9] text-[14px] ip:text-[16px] font-jakarta font-light placeholder-white text-white bg-opacity-30 h-[50px] rounded-[8px] pl-[12px] focus:outline-none"
                />

                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                  className="w-full bg-[#d9d9d9] text-white bg-opacity-30 h-[50px] rounded-[8px] pl-[12px] focus:outline-none"
                >
                  <option value="" disabled>Select Country</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>

                <select
                  value={category}
                  disabled
                  className="w-full bg-[#d9d9d9] text-white bg-opacity-30 h-[50px] rounded-[8px] pl-[12px] focus:outline-none opacity-60 cursor-not-allowed"
                >
                  <option value="" disabled>Select Business Category</option>
                  {categories.map((cat) => (
                    <option
                      className='border-none h-full text-black font-poppins font-regular'
                      key={cat}
                      value={cat}
                    >
                      {cat}
                    </option>
                  ))}
                </select>

                <input
                  type="password"
                  placeholder="Password (min 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-[#d9d9d9] placeholder-white text-white bg-opacity-30 h-[50px] rounded-[8px] pl-[12px] focus:outline-none"
                />

                <div className='flex flex-row items-center gap-[20px] mt-[25px] mb-[25px]'>
                  <input 
                    type="checkbox" 
                    id="terms-desktop" 
                    required 
                    className="w-[28px] h-[28px] rounded-[5px] bg-[#d9d9d9]"
                  />
                  <label htmlFor="terms-desktop" className='font-poppins text-[16px] text-white tracking-[1px] font-light'>
                    I agree to the <span className='underline text-[#7CD998]'>Terms & Condition</span>
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#1E7D6B] h-[50px] rounded-[8px] font-jakarta text-[16px] font-semibold tracking-[1px] text-white disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Creating business account...' : 'Create Business Account'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {errorMsg && (
          <p className="text-red-500 text-sm text-center mt-4">{errorMsg}</p>
        )}
        {successMessage && (
          <p className="text-green-500 text-sm text-center mt-4">{successMessage}</p>
        )}
      </div>
    </div>
  );
}
